import { motion } from "framer-motion";
import { useRef, useEffect } from "react";
import { GradualSpacing } from "@/components/ui/gradual-spacing";
import GlobalHeader from "@/components/GlobalHeader";
import boatsGraphic from "@/assets/yrd-boats-graphic.svg";
import { PHOTOS } from "@/lib/photos";
import { useMobileOnceInView } from "@/hooks/use-mobile-once-in-view";
import Footer from "@/components/Footer";
import { FileText } from "lucide-react";
import { usePageSEO } from "@/hooks/use-page-seo";

const images = {
  hero1: PHOTOS.FEMALE_ONLY.threeWomenDanceFloor,
  hero2: PHOTOS.DANCING.crowdCheeringCaptainHat,
};

const Terms = () => {
  usePageSEO({
    title: "Terms and Conditions — Yacht Rock Detroit 2026",
    description: "Review the terms and conditions for Yacht Rock Detroit 2026 ticket purchases and event attendance.",
  });

  const headerRef = useRef<HTMLElement>(null);
  const isHeaderInView = useMobileOnceInView(headerRef, { margin: "-100px" });

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
  }, []);

  return (
    <div className="min-h-screen bg-yacht-cream">
      <GlobalHeader heroRef={headerRef} />

      {/* Hero Header Section */}
      <section
        ref={headerRef}
        className="relative min-h-fit md:min-h-[70vh] bg-yacht-blue overflow-hidden flex flex-col md:flex-row md:items-end"
      >
        {/* Noise texture overlay */}
        <div
          className="absolute inset-0 opacity-[0.12] pointer-events-none"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
          }}
        />

        {/* Boats graphic background */}
        <img
          src={boatsGraphic}
          alt=""
          className="absolute pointer-events-none z-0
            w-[800%] left-1/2 -translate-x-1/2 top-0
            sm:w-[150%] sm:-top-[30%]
            lg:w-[90%] lg:-left-[10%] lg:-top-[30%] lg:translate-x-0"
          style={{ opacity: 0.15, filter: 'brightness(0) invert(1)' }}
        />

        {/* MOBILE LAYOUT */}
        <div className="md:hidden relative z-20 flex flex-col items-center pt-28 pb-8 px-6">
          <motion.p
            className="text-yacht-yellow/80 text-sm font-medium tracking-[0.3em] uppercase mb-3 text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={isHeaderInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.6 }}
          >
            Legal
          </motion.p>
          <div className="text-[12vw] font-bold text-white leading-[0.85] tracking-tight text-center mb-8" role="heading" aria-level={1}>
            <GradualSpacing text="Terms" duration={0.5} delayMultiple={0.08} />
          </div>

          {/* Images overlapping */}
          <div className="relative w-full max-w-xs h-[280px]">
            <motion.div
              className="absolute left-0 top-0 w-[55%] aspect-[3/4] rounded-2xl overflow-hidden shadow-2xl z-10"
              initial={{ opacity: 0, y: 30, rotate: -5 }}
              animate={isHeaderInView ? { opacity: 1, y: 0, rotate: -5 } : { opacity: 0, y: 30 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <img src={images.hero1} alt="Three women on the dance floor at Yacht Rock Detroit" className="w-full h-full object-cover" />
            </motion.div>
            <motion.div
              className="absolute right-0 top-6 w-[55%] aspect-[3/4] rounded-2xl overflow-hidden shadow-2xl z-20"
              initial={{ opacity: 0, y: 30, rotate: 5 }}
              animate={isHeaderInView ? { opacity: 1, y: 0, rotate: 5 } : { opacity: 0, y: 30 }}
              transition={{ duration: 0.8, delay: 0.5 }}
            >
              <img src={images.hero2} alt="Crowd cheering with captain hats" className="w-full h-full object-cover" />
            </motion.div>
          </div>
        </div>

        {/* DESKTOP LAYOUT */}
        <motion.div
          className="hidden md:block absolute top-44 right-[8%] w-[28%] max-w-sm aspect-[3/4] rounded-2xl overflow-hidden shadow-2xl"
          initial={{ opacity: 0, y: 50, rotate: 3 }}
          animate={isHeaderInView ? { opacity: 1, y: 0, rotate: 3 } : { opacity: 0, y: 50 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <img src={images.hero1} alt="Three women on the dance floor at Yacht Rock Detroit" className="w-full h-full object-cover" />
        </motion.div>

        <motion.div
          className="hidden md:block absolute bottom-20 right-[28%] w-[20%] max-w-xs aspect-square rounded-2xl overflow-hidden shadow-2xl z-10"
          initial={{ opacity: 0, y: 30, rotate: -5 }}
          animate={isHeaderInView ? { opacity: 1, y: 0, rotate: -5 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.8, delay: 0.5 }}
        >
          <img src={images.hero2} alt="Crowd cheering with captain hats" className="w-full h-full object-cover" />
        </motion.div>

        {/* Big Title - Bottom Left (Desktop only) */}
        <div className="hidden md:block relative z-20 px-8 md:px-16 pb-16 md:pb-20">
          <motion.p
            className="text-yacht-yellow/80 text-sm md:text-base font-medium tracking-[0.3em] uppercase mb-4"
            initial={{ opacity: 0, y: 20 }}
            animate={isHeaderInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.6 }}
          >
            Legal
          </motion.p>
          <h1 className="text-[10vw] lg:text-[8vw] font-bold text-white leading-[0.85] tracking-tight">
            <GradualSpacing text="Terms &" duration={0.5} delayMultiple={0.08} />
            <br />
            <GradualSpacing text="Conditions" duration={0.5} delayMultiple={0.06} />
          </h1>
        </div>
      </section>

      {/* Yellow Accent Strip */}
      <section className="relative py-6 bg-yacht-yellow overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
          }}
        />
        <div className="relative z-10 px-8 md:px-16">
          <p className="text-yacht-charcoal/80 text-lg md:text-xl font-medium text-center" style={{ fontFamily: 'Georgia, serif' }}>
            The fine print.
          </p>
        </div>
      </section>

      {/* Terms Content Section */}
      <section className="relative py-20 md:py-32 px-6 md:px-16 bg-yacht-cream overflow-hidden">
        {/* Decorative boat watermark */}
        <img
          src={boatsGraphic}
          alt=""
          className="absolute pointer-events-none z-0 w-[300%] md:w-[120%] left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 opacity-[0.04]"
        />

        <div className="relative z-10 max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 md:gap-16 items-center">
            {/* Text + Button Column */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.6 }}
            >
              <h2
                className="text-4xl md:text-5xl font-bold text-yacht-charcoal leading-tight mb-6"
                style={{ fontFamily: 'Georgia, serif' }}
              >
                Terms &<br />Conditions
              </h2>
              <p
                className="text-yacht-charcoal/70 text-lg md:text-xl leading-relaxed mb-8"
                style={{ fontFamily: 'Georgia, serif' }}
              >
                By purchasing a ticket or attending Yacht Rock Detroit, you agree to the event's Terms and Conditions. Please review them carefully before completing your purchase. All ticket sales are final, and entry is subject to compliance with all event rules and policies.
              </p>
              <a
                href="https://firebasestorage.googleapis.com/v0/b/yrd-2026.firebasestorage.app/o/Yacht-Rock-Detroit-Terms-and-Conditions-2026.docx.pdf?alt=media"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 bg-yacht-yellow text-yacht-charcoal font-semibold text-lg px-8 py-4 rounded-full hover:brightness-95 transition-all duration-200 shadow-md hover:shadow-lg"
              >
                <FileText className="w-5 h-5" />
                View Terms
              </a>
            </motion.div>

            {/* Photo Collage Column */}
            <div className="relative h-[400px] md:h-[500px]">
              <motion.div
                className="absolute top-0 right-0 w-[60%] aspect-[3/4] rounded-2xl overflow-hidden shadow-2xl z-10"
                initial={{ opacity: 0, y: 40, rotate: 3 }}
                whileInView={{ opacity: 1, y: 0, rotate: 3 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.7, delay: 0.2 }}
              >
                <img src={PHOTOS.BOAT.groupSixPeopleWaving} alt="Friends on a boat at Yacht Rock Detroit" className="w-full h-full object-cover" />
              </motion.div>
              <motion.div
                className="absolute bottom-0 left-0 w-[55%] aspect-square rounded-2xl overflow-hidden shadow-2xl z-20"
                initial={{ opacity: 0, y: 40, rotate: -4 }}
                whileInView={{ opacity: 1, y: 0, rotate: -4 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.7, delay: 0.4 }}
              >
                <img src={PHOTOS.GROUP.eightPeopleGazebo} alt="Group gathering at Yacht Rock Detroit" className="w-full h-full object-cover" />
              </motion.div>
              {/* Decorative accent */}
              <div className="absolute top-[15%] left-[20%] w-24 h-24 rounded-full bg-yacht-blue/10 z-0" />
              <div className="absolute bottom-[20%] right-[15%] w-16 h-16 rounded-full bg-yacht-yellow/20 z-0" />
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Terms;
